import {
  InlineMessage,
  LocalizedString,
  NonNegativeInteger,
  ReadOnlyMoneyAmountField as ReadOnlyMoneyAmountFieldExt,
  ReadOnlyValue,
  useComponentsWrapperContext,
  useUniqueID,
} from "@dbn/omnichannel-design-system-g4p";
import { ComponentProps } from "react";
import { MoneyAmount } from "../../globalDomain";
import { DisplayMoneyAmount } from "../Price/DisplayMoneyAmount";
import { useFormatMessage, useFormatMoneyAmount } from "../../intl";
import { useTooltipAccessibility } from "../Tooltip/Tooltip";

type Props = {
  label: LocalizedString;
  value: MoneyAmount;
  inverted?: boolean;
  size: "small" | "x-small" | "input" | "large" | "big";
  fractionDigits?: NonNegativeInteger;
  description?: LocalizedString;
  descriptionAriaLabel?: LocalizedString;
  tooltipCloseIconAriaLabel?: LocalizedString;
  feedback?: {
    type: ComponentProps<typeof InlineMessage>["type"];
    message: LocalizedString;
  };
  forceDisplayMoneyAmount?: boolean;
};

export function ReadOnlyMoneyAmountField(props: Props) {
  const labelId = useUniqueID();
  const { componentType } = useComponentsWrapperContext();
  const formatMessage = useFormatMessage();
  const formatMoneyAmount = useFormatMoneyAmount(props.fractionDigits);
  const tooltipAccessibility = useTooltipAccessibility(
    componentType,
    props.description,
    props.descriptionAriaLabel,
    props.tooltipCloseIconAriaLabel
  );

  return (!componentType || componentType === "g4p") &&
    !props.forceDisplayMoneyAmount ? (
    formatMoneyAmount(props.value, ({ amount, currency }) => (
      <ReadOnlyValue
        label={props.label}
        value={formatMessage("MoneyAmount", {
          amount: amount,
          currency: currency,
        })}
        description={props.description}
        {...tooltipAccessibility}
      />
    ))
  ) : (
    <ReadOnlyMoneyAmountFieldExt
      label={props.label}
      description={props.description}
      {...tooltipAccessibility}
      feedback={props.feedback}
      inverted={props.inverted}
      size={props.size}
      moneyAmount={
        <DisplayMoneyAmount
          id={labelId}
          amount={props.value}
          size={props.size}
          inverted={props.inverted}
          fractionDigits={props.fractionDigits}
        />
      }
    />
  );
}
